'use client';

import React from 'react';
import { Button, Input, Link, Checkbox, Form } from '@heroui/react';
import { Icon } from '@iconify/react';
import { Formik, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router';
import RightImage from './imagecreator.jpg';
import { AcmeIcon } from './acme';
import { parseApiError } from '../../utils/errorParser';
import './index.css';
import IcUserID from './User Id.svg';
import { AlertBlock, Typography } from '../../components';
import { LogoCreatorAuthIc } from '../../components/icons';
import { useTranslations } from '../../contexts/translation.context';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

type SignInFormValues = {
  email: string;
  password: string;
  remember: boolean;
};

const CLIENT_REMEMBER_KEY = 'clientRememberMe';
const CLIENT_CREDENTIALS_KEY = 'clientCredentials';
const LEGACY_REMEMBER_KEY = 'rememberMe';

const getStoredClientValues = (): SignInFormValues => {
  if (typeof window === 'undefined') {
    return { email: '', password: '', remember: false };
  }

  const rememberRaw =
    localStorage.getItem(CLIENT_REMEMBER_KEY) ?? localStorage.getItem(LEGACY_REMEMBER_KEY);
  const remember = rememberRaw === 'true';

  if (!remember) {
    return { email: '', password: '', remember: false };
  }

  const storedCredentials = localStorage.getItem(CLIENT_CREDENTIALS_KEY);
  if (storedCredentials) {
    try {
      const parsed = JSON.parse(storedCredentials) ?? {};
      return {
        email: typeof parsed.email === 'string' ? parsed.email : '',
        password: typeof parsed.password === 'string' ? parsed.password : '',
        remember: true,
      };
    } catch {
      localStorage.removeItem(CLIENT_CREDENTIALS_KEY);
    }
  }

  return { email: '', password: '', remember: true };
};

const persistClientValues = (values: SignInFormValues) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.removeItem(LEGACY_REMEMBER_KEY);

  if (values.remember) {
    localStorage.setItem(CLIENT_REMEMBER_KEY, 'true');
    localStorage.setItem(
      CLIENT_CREDENTIALS_KEY,
      JSON.stringify({
        email: values.email ?? '',
        password: values.password ?? '',
      })
    );
  } else {
    localStorage.setItem(CLIENT_REMEMBER_KEY, 'false');
    localStorage.removeItem(CLIENT_CREDENTIALS_KEY);
  }
};
// Validation schema for client sign in - UPDATED v2
const getSignInSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('auth.validation.emailInvalid'))
      .required(t('auth.validation.emailRequired')),
    password: Yup.string()
      .min(6, t('auth.validation.passwordMin'))
      .max(25, t('auth.validation.passwordMax'))
      .required(t('auth.validation.passwordRequired')),
    remember: Yup.boolean(),
  });

export default function SignIn() {
  const { t } = useTranslations();
  const [isVisible, setIsVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [userType, setUserType] = React.useState<'creator' | 'marketer' | null>(null);
  const [currentStep, setCurrentStep] = React.useState<'select' | 'signin'>('select');
  const [initialFormValues] = React.useState<SignInFormValues>(() => getStoredClientValues());
  const lastEnteredValuesRef = React.useRef<SignInFormValues>(initialFormValues);
  const updateRememberedValues = React.useCallback((nextValues: SignInFormValues) => {
    lastEnteredValuesRef.current = nextValues;
    persistClientValues(nextValues);
  }, []);

  const { login, googleAuth, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Авторизация по токену из query-параметра
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      loginWithToken(token, 'client')
        .then(() => {
          navigate('/client/dashboard', { replace: true });
        })
        .catch((err) => {
          setError(t('auth.errors.tokenAuthError'));
        });
    }
    // eslint-disable-next-line
  }, [location.search]);

  React.useEffect(() => {
    if (location.state?.userType) {
      setUserType(location.state.userType);
      setCurrentStep('signin');
      navigate(location.pathname, { replace: true });
    } else if (location.state?.skipSelect) {
      setUserType('creator');
      setCurrentStep('signin');
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  React.useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const toggleVisibility = () => setIsVisible(!isVisible);

  const handleUserTypeSelect = (type: 'creator' | 'marketer') => {
    if (type === 'marketer') {
      navigate('/partner/sign-in', { state: { skipSelect: true } });
      return;
    }
    setUserType(type);
    setCurrentStep('signin');
  };

  const handleSubmit = async (
    values: SignInFormValues,
    { setSubmitting, setValues }: FormikHelpers<SignInFormValues>
  ) => {
    const submittedValues = { ...values };
    lastEnteredValuesRef.current = submittedValues;
    try {
      setIsLoading(true);
      setError(null);

      await login(values.email, values.password, 'client');
      navigate('/client/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Login failed:', error);
      setError(parseApiError(error, t('auth.errors.loginError')));
      setTimeout(() => {
        setValues(submittedValues, false);
      }, 0);
    } finally {
      updateRememberedValues(lastEnteredValuesRef.current);
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const getUserTypeTitle = () => {
    return userType === 'creator' ? t('auth.userTypes.creator') : t('auth.userTypes.marketer');
  };
  const userTypeTitle = getUserTypeTitle();

  return (
    <div className="relative min-h-screen w-full bg-white">
      {!(error && success) && (
        <div className="absolute top-12 left-12 hidden items-center md:flex">
          <LogoCreatorAuthIc />
        </div>
      )}

      <div
        className={`absolute top-12 right-12 hidden md:block ${currentStep !== 'select' ? 'md:hidden' : ''}`}
      >
        <LanguageSwitcher variant="ghost" />
      </div>

      <div className="container flex min-h-screen w-full flex-col pt-[5.25rem] pb-4 md:py-6">
        <div className="md:hidden flex flex-col justify-end fixed top-0 left-0 w-full z-50 bg-white border-b border-solid border-neutral-12 px-4 py-6">
          <div className="flex items-center justify-between gap-4">
            {!(error && success) && (
              <div className="flex items-center justify-center w-[4.75rem] h-[1.375rem] shrink-0">
                <LogoCreatorAuthIc className="size-full" />
              </div>
            )}

            <LanguageSwitcher variant="ghost" />
          </div>
        </div>

        <div className="flex w-full flex-1 flex-col justify-start md:min-h-[calc(100vh-48px)] md:flex-row md:items-stretch md:justify-center">
          <div
            className={`flex w-full flex-col items-center bg-background py-6 md:justify-center md:px-6 md:py-12 ${
              currentStep === 'select' ? 'md:w-full' : 'md:flex-1'
            }`}
          >
            <div className="flex w-full flex-col items-center gap-8 md:gap-6">
              <div className="w-full max-w-[26.5rem] md:hidden">
                <div
                  className="h-[8.75rem] md:h-[12.5rem] w-full overflow-hidden rounded-2xl md:rounded-3xl bg-cover bg-top md:bg-center"
                  style={{
                    backgroundImage: `url(${RightImage})`,
                  }}
                />
              </div>
              {currentStep === 'select' ? (
                <ClientAuthSelectStep
                  t={t}
                  onSelectUserType={handleUserTypeSelect}
                  onNavigateSignUp={() => navigate('/client/sign-up')}
                />
              ) : (
                <ClientSignInFormStep
                  t={t}
                  error={error}
                  success={success}
                  isLoading={isLoading}
                  initialValues={initialFormValues}
                  onSubmit={handleSubmit}
                  updateRememberedValues={updateRememberedValues}
                  googleAuth={googleAuth}
                  userType={userType}
                  userTypeTitle={userTypeTitle}
                  onNavigatePartnerSignIn={() =>
                    navigate('/partner/sign-in', { state: { skipSelect: true } })
                  }
                  onSetUserType={(type) => setUserType(type)}
                />
              )}
            </div>
          </div>

          {currentStep !== 'select' && (
            <div className="hidden w-full items-stretch gap-12 md:flex md:flex-1">
              <div className="shrink-0">
                <LanguageSwitcher variant="ghost" className="shrink-0" />
              </div>
              <div
                className="hidden h-full flex-1 overflow-hidden rounded-8 md:block"
                style={{
                  backgroundImage: `url(${RightImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type ClientAuthSelectStepProps = {
  t: (key: string) => string;
  onSelectUserType: (type: 'creator' | 'marketer') => void;
  onNavigateSignUp: () => void;
};

const ClientAuthSelectStep: React.FC<ClientAuthSelectStepProps> = React.memo(
  ({ t, onSelectUserType, onNavigateSignUp }) => (
    <div className="flex w-full max-w-[26.5rem] flex-col items-center gap-6 md:gap-8">
      <div className="flex flex-col w-full text-center gap-2">
        <Typography variant="h3">{t('auth.login')}</Typography>
        <Typography className="text-auth-muted" variant="body1">
          {t('auth.selectAccountType')}
        </Typography>
      </div>

      <Form className="flex w-full flex-col gap-2" aria-label={t('auth.selectUserType')}>
        <div
          onClick={() => onSelectUserType('creator')}
          className="user-type-card flex min-h-[4.75rem] w-full cursor-pointer items-center rounded-lg bg-auth-surface p-4 transition-colors hover:bg-auth-surface-hover"
        >
          <div className="flex items-center gap-2">
            <div className="flex size-6 md:size-9 items-center justify-center">
              <Icon
                width={36}
                height={36}
                icon="solar:user-rounded-linear"
                className="text-2xl text-auth-ink"
              />
            </div>
            <div className="flex flex-col">
              <Typography variant="h7">
                {t('sidebar.creator')} / {t('sidebar.agency')}
              </Typography>
            </div>
          </div>
        </div>

        <div
          onClick={() => onSelectUserType('marketer')}
          className="user-type-card flex min-h-[4.75rem] w-full cursor-pointer items-center rounded-lg bg-auth-surface px-4 py-5 transition-colors hover:bg-auth-surface-hover"
        >
          <div className="flex items-center gap-2">
            <div className="flex size-6 md:size-9 items-center justify-center">
              <img
                className="size-full object-contain"
                width={36}
                height={36}
                src={IcUserID}
                alt=""
              />
            </div>
            <div className="flex flex-col">
              <Typography variant="h7">{t('sidebar.marketer')}</Typography>
            </div>
          </div>
        </div>
      </Form>

      <div className="w-full flex items-center gap-2 flex-wrap justify-between">
        <p className="text-sm text-auth-muted">{t('auth.noAccount')}</p>
        <Button
          variant="light"
          size="sm"
          onPress={onNavigateSignUp}
          className="h-5 min-w-0 p-0 text-sm text-partner"
        >
          {t('auth.signUp')}
        </Button>
      </div>
    </div>
  )
);

type ClientSignInFormStepProps = {
  t: (key: string) => string;
  error: string | null;
  success: string | null;
  isLoading: boolean;
  initialValues: SignInFormValues;
  onSubmit: (values: SignInFormValues, helpers: FormikHelpers<SignInFormValues>) => Promise<void>;
  updateRememberedValues: (values: SignInFormValues) => void;
  googleAuth: () => void;
  userType: 'creator' | 'marketer' | null;
  userTypeTitle: string;
  onNavigatePartnerSignIn: () => void;
  onSetUserType: (type: 'creator' | 'marketer' | null) => void;
};

const ClientSignInFormStep: React.FC<ClientSignInFormStepProps> = React.memo(
  ({
    t,
    error,
    success,
    isLoading,
    initialValues,
    onSubmit,
    updateRememberedValues,
    googleAuth,
    userType,
    userTypeTitle,
    onNavigatePartnerSignIn,
    onSetUserType,
  }) => {
    const [localIsVisible, setLocalIsVisible] = React.useState(false);

    const localToggleVisibility = () => setLocalIsVisible(!localIsVisible);

    return (
      <div className="flex w-full max-w-[26.5rem] flex-col items-center gap-2 md:gap-4">
        <div className="w-full flex flex-col text-center gap-1 md:gap-2 mb-4">
          <Typography variant="h3">
            {t('auth.login')} / {userTypeTitle}
          </Typography>
          <Typography className="text-auth-muted" variant="body1">
            {t('auth.enterData')}
          </Typography>
        </div>
        {error && <AlertBlock type="rounded" data={[{ severity: 'error', description: error }]} />}
        {success && (
          <AlertBlock
            type="rounded"
            data={[{ severity: 'success', description: success }]}
            className="w-full mb-4"
          />
        )}
        <Formik<SignInFormValues>
          key="signin-form"
          initialValues={initialValues}
          validationSchema={getSignInSchema(t)}
          onSubmit={onSubmit}
          enableReinitialize={false}
        >
          {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
            <form
              className="flex w-full flex-col gap-4"
              onSubmit={handleSubmit}
              noValidate={true}
              lang="ru"
              data-disable-validation="true"
              autoComplete="off"
              onInvalid={(e) => e.preventDefault()}
            >
              <div className="flex flex-col gap-1 w-full">
                <Input
                  label={t('labels.email')}
                  name="email"
                  placeholder={t('placeholders.enterEmail')}
                  type="email"
                  variant="bordered"
                  value={values.email}
                  onChange={(e) => {
                    if (e.nativeEvent?.isTrusted === false) {
                      return;
                    }
                    const nextEmail = e.target.value;
                    setFieldValue('email', nextEmail);
                    updateRememberedValues({
                      ...values,
                      email: nextEmail,
                    });
                  }}
                  labelPlacement="inside"
                  className="w-full custom-required-label"
                  classNames={{
                    inputWrapper: 'h-14',
                    input: 'text-black',
                  }}
                  autoComplete="email"
                  spellCheck="false"
                  data-no-validate="true"
                  inputMode="email"
                  onInvalid={(e) => e.preventDefault()}
                  onInput={(e) => {
                    (e.target as HTMLInputElement).setCustomValidity('');
                  }}
                  title=""
                  required={false}
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-xs" />
              </div>

              <div className="flex flex-col gap-1 w-full">
                <Input
                  endContent={
                    <button type="button" onClick={localToggleVisibility}>
                      {localIsVisible ? (
                        <Icon
                          className="pointer-events-none text-2xl text-default-400"
                          icon="solar:eye-closed-linear"
                        />
                      ) : (
                        <Icon
                          className="pointer-events-none text-2xl text-default-400"
                          icon="solar:eye-bold"
                        />
                      )}
                    </button>
                  }
                  label={t('labels.password')}
                  name="password"
                  placeholder={t('placeholders.enterPassword')}
                  type={localIsVisible ? 'text' : 'password'}
                  variant="bordered"
                  value={values.password}
                  onChange={(e) => {
                    if (e.nativeEvent?.isTrusted === false) {
                      return;
                    }
                    const nextPassword = e.target.value;
                    setFieldValue('password', nextPassword);
                    updateRememberedValues({
                      ...values,
                      password: nextPassword,
                    });
                  }}
                  labelPlacement="inside"
                  className="w-full custom-required-label"
                  classNames={{
                    inputWrapper: 'h-14',
                    input: 'text-black',
                  }}
                  autoComplete="current-password"
                  spellCheck="false"
                  data-no-validate="true"
                  onInvalid={(e) => e.preventDefault()}
                  onInput={(e) => {
                    (e.target as HTMLInputElement).setCustomValidity('');
                  }}
                  pattern=".*"
                  title=""
                  required={false}
                />
                <ErrorMessage name="password" component="div" className="text-red-500 text-xs" />
              </div>

              <div className="flex w-full items-center flex-wrap gap-2 px-1 justify-between">
                <Checkbox
                  name="remember"
                  size="sm"
                  isSelected={values.remember}
                  className="text-white"
                  onValueChange={(checked) => {
                    const nextValues = {
                      ...values,
                      remember: checked,
                    };
                    setFieldValue('remember', checked);
                    updateRememberedValues(nextValues);
                  }}
                >
                  {t('auth.rememberMe')}
                </Checkbox>
                <Link href="/client/forgot-password" size="sm">
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <Button
                className="w-full text-white font-semibold h-12 mt-4 md:mt-2 text-base"
                color="primary"
                type="submit"
                isLoading={isLoading || isSubmitting}
                disabled={isLoading || isSubmitting}
              >
                {isLoading || isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
            </form>
          )}
        </Formik>
        <div className="flex w-full flex-col gap-2">
          <Button
            startContent={<Icon icon="flat-color-icons:google" width={24} />}
            variant="bordered"
            className="h-12 font-semibold text-base"
            onPress={googleAuth}
          >
            {t('auth.continueWithGoogle')}
          </Button>
        </div>
        <div className="flex w-full flex-col gap-2">
          <div
            onClick={() => {
              if (userType === 'creator') {
                onNavigatePartnerSignIn();
              } else {
                onSetUserType('creator');
              }
            }}
            className="user-type-card flex h-12 w-full cursor-pointer items-center justify-center rounded-lg bg-auth-surface p-3 transition-colors hover:bg-auth-surface-hover"
          >
            <div className="flex items-center gap-2">
              <p className="text-base font-semibold text-auth-ink">{t('auth.loginAsMarketer')}</p>
            </div>
          </div>
        </div>
        <div className="w-full flex items-center gap-2 flex-wrap justify-between mt-4 md:mt-0">
          <p className="text-small text-auth-muted">{t('auth.noAccount')}&nbsp;</p>
          <Link href="/client/sign-up" size="sm">
            {t('auth.signUp')}
          </Link>
        </div>
      </div>
    );
  }
);
